import { getStoredAdminToken } from 'containers/Admin/adminSession';

import api, { ADMIN_AUTH_TOKEN_HEADER } from './index';

import {
  CreateUserFeedbackRequest,
  UpdateUserFeedbackJiraConfigRequest,
  UserFeedbackJiraConfig,
  UserFeedbackJiraLinks,
  UserFeedbackRecord,
  UserFeedbackStatus,
} from 'features/userFeedback/types';

type GetUserFeedbackRequest = {
  scope?: 'all' | 'mine';
};

type UpdateFeedbackStatusRequest = {
  feedbackId: string;
  status: UserFeedbackStatus;
};

type SyncFeedbackToJiraRequest = {
  feedbackId: string;
};

const getAdminFeedbackHeaders = () => {
  const adminToken = getStoredAdminToken();
  return adminToken ? { [ADMIN_AUTH_TOKEN_HEADER]: adminToken } : undefined;
};

const userFeedbackApi = api.injectEndpoints({
  endpoints: build => ({
    getUserFeedback: build.query<UserFeedbackRecord[], GetUserFeedbackRequest | void>({
      query: params => ({
        method: 'GET',
        url:
          params && params.scope === 'mine'
            ? '/api/v1/user-feedback?scope=mine'
            : '/api/v1/user-feedback',
        headers:
          params && params.scope === 'mine'
            ? undefined
            : getAdminFeedbackHeaders(),
      }),
      providesTags: result =>
        result
          ? [
              { type: 'userFeedback', id: 'LIST' },
              ...result.map(item => ({
                type: 'userFeedback' as const,
                id: item.id,
              })),
            ]
          : [{ type: 'userFeedback', id: 'LIST' }],
    }),
    submitUserFeedback: build.mutation<
      UserFeedbackRecord,
      CreateUserFeedbackRequest
    >({
      query: body => ({
        method: 'POST',
        url: '/api/v1/user-feedback',
        body,
      }),
      invalidatesTags: [{ type: 'userFeedback', id: 'LIST' }],
    }),
    updateUserFeedbackStatus: build.mutation<
      UserFeedbackRecord,
      UpdateFeedbackStatusRequest
    >({
      query: ({ feedbackId, status }) => ({
        method: 'PATCH',
        url: `/api/v1/user-feedback/${feedbackId}`,
        body: { status },
        headers: getAdminFeedbackHeaders(),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'userFeedback', id: 'LIST' },
        { type: 'userFeedback', id: arg.feedbackId },
      ],
    }),
    getUserFeedbackJiraConfig: build.query<UserFeedbackJiraConfig, void>({
      query: () => ({
        method: 'GET',
        url: '/api/v1/user-feedback/jira-config',
        headers: getAdminFeedbackHeaders(),
      }),
      providesTags: [{ type: 'userFeedback', id: 'JIRA_CONFIG' }],
    }),
    getUserFeedbackJiraLinks: build.query<UserFeedbackJiraLinks, void>({
      query: () => ({
        method: 'GET',
        url: '/api/v1/user-feedback/jira-links',
      }),
      providesTags: [{ type: 'userFeedback', id: 'JIRA_CONFIG' }],
    }),
    updateUserFeedbackJiraConfig: build.mutation<
      UserFeedbackJiraConfig,
      UpdateUserFeedbackJiraConfigRequest
    >({
      query: body => ({
        method: 'PUT',
        url: '/api/v1/user-feedback/jira-config',
        body,
        headers: getAdminFeedbackHeaders(),
      }),
      invalidatesTags: [{ type: 'userFeedback', id: 'JIRA_CONFIG' }],
    }),
    syncFeedbackToJira: build.mutation<
      UserFeedbackRecord,
      SyncFeedbackToJiraRequest
    >({
      query: ({ feedbackId }) => ({
        method: 'POST',
        url: `/api/v1/user-feedback/${feedbackId}/jira-sync`,
        headers: getAdminFeedbackHeaders(),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'userFeedback', id: 'LIST' },
        { type: 'userFeedback', id: arg.feedbackId },
      ],
    }),
  }),
});

export const {
  useGetUserFeedbackJiraConfigQuery,
  useGetUserFeedbackJiraLinksQuery,
  useGetUserFeedbackQuery,
  useSyncFeedbackToJiraMutation,
  useSubmitUserFeedbackMutation,
  useUpdateUserFeedbackJiraConfigMutation,
  useUpdateUserFeedbackStatusMutation,
} = userFeedbackApi;
